import { useCallback, useEffect, useRef } from 'react';
import { calculatorLogger, CalculatorMetrics } from '@/utils/logging/calculatorLogger';

/**
 * Hook for calculator logging and performance monitoring
 */
export function useCalculatorLogger(componentName: string = 'Calculator') {
  const mountTimeRef = useRef(Date.now());
  const renderCountRef = useRef(0);

  // Track renders
  useEffect(() => {
    renderCountRef.current++;
  });

  // Log component lifecycle
  useEffect(() => {
    calculatorLogger.logPerformance(`${componentName}:mount`, 0);
    
    return () => {
      const metrics = calculatorLogger.getMetrics();
      console.log(
        `%c[${componentName}] Session Summary`,
        'color: #6366f1; font-weight: bold',
        {
          duration: `${((Date.now() - mountTimeRef.current) / 1000).toFixed(1)}s`,
          renders: renderCountRef.current,
          totalChanges: metrics.totalChanges,
          avgCalcTime: `${metrics.avgCalculationTime.toFixed(2)}ms`,
          peakRate: `${metrics.peakChangesPerSecond}/s`,
          circuitBreakers: metrics.circuitBreakerActivations,
        }
      );
    };
  }, [componentName]);

  /**
   * Log a state change with source tracking
   */
  const logChange = useCallback((
    field: string,
    oldValue: any,
    newValue: any,
    source: string = componentName
  ) => {
    calculatorLogger.logStateChange(field, oldValue, newValue, source);
  }, [componentName]);

  /**
   * Wrap a calculation function with performance tracking
   */
  const trackCalculation = useCallback(<T>(
    operationName: string,
    fn: () => T
  ): T => {
    const start = performance.now();
    const result = fn();
    const duration = performance.now() - start;
    calculatorLogger.logPerformance(`calculation:${operationName}`, duration);
    return result;
  }, []);

  /**
   * Log a circuit breaker activation
   */
  const logCircuitBreaker = useCallback((field: string, reason: string) => {
    calculatorLogger.logCircuitBreaker(field, reason);
  }, []);

  /**
   * Log hydration from draft/storage
   */
  const logHydration = useCallback((source: string, fieldCount: number, startTime: number) => {
    calculatorLogger.logHydration(source, fieldCount, performance.now() - startTime);
  }, []);

  /**
   * Log save to draft/storage
   */
  const logSave = useCallback((destination: string, fieldCount: number, startTime: number) => {
    calculatorLogger.logSave(destination, fieldCount, performance.now() - startTime);
  }, []);

  /**
   * Get current metrics
   */
  const getMetrics = useCallback((): CalculatorMetrics => {
    return calculatorLogger.getMetrics();
  }, []);

  /**
   * Clear all metrics
   */
  const clearMetrics = useCallback(() => {
    calculatorLogger.clear();
  }, []);

  return {
    logChange,
    trackCalculation,
    logCircuitBreaker,
    logHydration,
    logSave,
    getMetrics,
    clearMetrics,
  };
}
